import {
  type AvailabilityRequest,
  AvailabilityRequestEnum,
  type AvailabilityResponse,
  type Lodging,
  LodgingCollection,
} from "@b2bportal/lodging-api";

export type AvailabilityFetcher = (
  request: AvailabilityRequest
) => Promise<AvailabilityResponse>;

/** Stops a server that keeps issuing tokens from spinning us forever. */
export const MAX_AVAILABILITY_PAGES = 10;

const buildNextPageRequest = (moreToken: string): AvailabilityRequest => ({
  progressiveConfig: {},
  excludeNonLuxuryLodgings: false,
  AvailabilityRequest: AvailabilityRequestEnum.FollowUpSearch,
  moreToken,
  lodgingCollection: LodgingCollection.NoCollection,
});

/**
 * Availability returns one page of lodgings at a time, plus a `nextPageToken`
 * when more exist. Walks those tokens and returns the initial response with
 * every page's lodgings merged in.
 */
export const fetchAvailabilityAllPages = async (
  initialRequest: AvailabilityRequest,
  fetchPage: AvailabilityFetcher
): Promise<AvailabilityResponse> => {
  const initialResponse = await fetchPage(initialRequest);

  const lodgings: Lodging[] = [...(initialResponse.lodgings ?? [])];
  const seenIds = new Set(lodgings.map((entry) => entry.lodging.id));

  let nextPageToken = initialResponse.nextPageToken;
  let pagesFetched = 1;

  while (nextPageToken != null && pagesFetched < MAX_AVAILABILITY_PAGES) {
    let nextPage: AvailabilityResponse;
    try {
      nextPage = await fetchPage(buildNextPageRequest(nextPageToken));
    } catch (error) {
      // Keep the pages already gathered so the user still sees partial
      // results rather than an error screen.
      console.error("Failed to fetch the next availability page:", error);
      break;
    }

    // A repeat across pages would collide on the offers list's React key.
    for (const entry of nextPage.lodgings ?? []) {
      if (seenIds.has(entry.lodging.id)) continue;
      seenIds.add(entry.lodging.id);
      lodgings.push(entry);
    }

    nextPageToken = nextPage.nextPageToken;
    pagesFetched += 1;
  }

  return { ...initialResponse, lodgings };
};
